import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CustomNavbar from "components/users/CustomNavbar";
import CustomBackButton from "components/general/CustomBackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faPlus,
  faQuestionCircle,
  faTimesCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { primaryColor, secondaryColor } from "utils/colors";
import { convertToBase64 } from "utils/imagesAndColors";
import logo from "assets/logo.png";
import ImageModal from "components/general/ImageModal";
import ConfirmationModal from "components/general/ConfirmationModal";
import { createAuthenticatedAxiosInstance, createCategory } from "api/auth";
import { useAuthStore } from "../../../zustand/loginStore";
import CancelContinue from "components/general/CancelContinueModal";

const AddCategory: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [categoryName, setCategoryName] = useState("");
  const [image, setImage] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "Hubo un error al crear la categoría. Por favor, inténtalo de nuevo."
  );

  /**
   * Handles image file selection and conversion to base64
   * @function onImageSelect
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file input change event
   *
   * Converts selected image to base64 and updates state
   */
  const onImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const base64Image = await convertToBase64(files[0]);
      setImage(base64Image);
    }
  };

  /**
   * Resets form to initial state
   * @function handleReset
   *
   * Clears category name, image, and validation errors
   */
  const handleReset = () => {
    setCategoryName("");
    setImage("");
    setNameError(false);
    setImageError(false);
  };

  /**
   * Validates form fields
   * @function validateForm
   * @returns {boolean} True if form is valid, false otherwise
   *
   * Checks required fields and sets error states
   */
  const validateForm = () => {
    let isValid = true;

    if (!categoryName.trim()) {
      setNameError(true);
      isValid = false;
    }

    if (!image) {
      setImageError(true);
      isValid = false;
    }

    return isValid;
  };

  /**
   * Removes the current image
   * @function handleDeleteImage
   *
   * Resets image state to empty
   */
  const handleDeleteImage = () => {
    setImage("");
  };

  /**
   * Handles category creation
   * @function handleSave
   * @async
   *
   * Validates form and sends API request to create category
   */
  const handleSave = async () => {
    setNameError(false);
    setImageError(false);

    if (!validateForm()) return;

    try {
      const response = await createCategory(axiosInstance, {
        name: categoryName,
        images: [image],
      });
      if (response.status === 201 || response.status === 200) {
        setShowSuccessModal(true);
      } else {
        if (
          response?.data?.errors?.["name"][0] ===
          "The name has already been taken."
        ) {
          setErrorMessage(
            "Este nombre ya está registrado, por favor intenta con uno diferente"
          );
        } else {
          setErrorMessage(
            "Hubo un error al crear la categoría. Por favor, inténtalo de nuevo."
          );
        }
        setShowErrorModal(true);
      }
    } catch (error) {
      setShowErrorModal(true);
    }
  };

  /**
   * Shows reset confirmation modal
   * @function handleResetClick
   */
  const handleResetClick = () => {
    setShowResetModal(true);
  };

  /**
   * Shows save confirmation modal
   * @function handleSaveClick
   */
  const handleSaveClick = () => {
    setShowSaveModal(true);
  };

  /**
   * Confirms form reset action
   * @function confirmReset
   */
  const confirmReset = () => {
    handleReset();
    setShowResetModal(false);
  };

  /**
   * Confirms category save action
   * @function confirmSave
   * @async
   */
  const confirmSave = async () => {
    setShowSaveModal(false);
    await handleSave();
  };

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={() => navigate("/inventories/categories")} />
      <Container className="px-4 px-md-5" style={{ maxWidth: "1000px" }}>
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img src={logo} alt="Logo" style={{ width: "175px" }} />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1>Agregar categoría</h1>
          </Col>
        </Row>
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Nombre de la categoría</Form.Label>
            <Form.Control
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Nombre de la categoría"
              isInvalid={nameError}
            />
            {nameError && (
              <Form.Control.Feedback type="invalid">
                El nombre de la categoría es requerido
              </Form.Control.Feedback>
            )}
          </Form.Group>
          {imageError && (
            <div className="text-danger text-center mt-2">
              La imagen es requerida
            </div>
          )}
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">
              Imagen representativa
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip className="custom-tooltip">
                    Imagen que se mostrará para identificar la categoría
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
              </OverlayTrigger>
            </Form.Label>

            <div className="d-flex justify-content-center">
              {image ? (
                <div
                  style={{
                    width: "9.5rem",
                    height: "9.5rem",
                    borderRadius: "9px",
                    overflow: "hidden",
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onClick={() => setModalOpen(true)}
                >
                  <img
                    src={`data:image/jpeg;base64,${image}`}
                    alt="Category"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Button
                    variant="link"
                    style={{
                      position: "absolute",
                      top: "0.1rem",
                      right: "0.1rem",
                      color: "#614900",
                      borderRadius: "50%",
                      padding: "8px",
                      fontSize: "1.2rem",
                      zIndex: 10,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage();
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              ) : (
                <label style={{ cursor: "pointer" }}>
                  <div
                    style={{
                      border: "2px solid" + secondaryColor,
                      borderRadius: "9px",
                      backgroundColor: secondaryColor,
                      width: "9.5rem",
                      height: "9.5rem",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: primaryColor,
                        borderRadius: "50%",
                        width: "8rem",
                        height: "8rem",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faPlus}
                        color="white"
                        style={{ height: "7rem", fontWeight: "lighter" }}
                      />
                    </div>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={onImageSelect}
                      style={{ display: "none" }}
                    />
                  </div>
                </label>
              )}
            </div>
          </Form.Group>
          <Row className="mt-4 mb-5">
            <Col>
              <Button variant="secondary" onClick={handleResetClick}>
                Restablecer formulario
              </Button>
            </Col>
            <Col className="text-end">
              <Button variant="primary" onClick={handleSaveClick}>
                Guardar categoría
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
      <ImageModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        images={[image]}
        selectedIndex={0}
        onSelectImage={() => {}}
        isReadOnly={true}
        isSingleImage={true}
      />
      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          navigate("/inventories/categories");
        }}
        title="Éxito"
        message="La categoría se ha creado exitosamente"
        icon={faCheckCircle}
        iconColor="text-success"
      />

      <ConfirmationModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        icon={faTimesCircle}
        iconColor="text-danger"
      />
      <CancelContinue
        show={showResetModal}
        onHide={() => setShowResetModal(false)}
        onConfirm={confirmReset}
        title="Atención"
        message="¿Estás seguro de restablecer la información? Los datos no guardados no surtirán efecto."
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />

      <CancelContinue
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        onConfirm={confirmSave}
        title="Atención"
        message="¿Estás seguro de agregar una nueva categoría?"
        icon={faQuestionCircle}
        iconColor="text-warning"
      />
    </>
  );
};

export default AddCategory;
